import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { HomeEvalResult } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { HomeEvalAiContextCard, HomeEvalAiResponseDetails } from './HomeEvalAiDetails';
import { QueryEvalDetails } from './QueryEvalDetails';

/**
 * One home evaluation result: why a query created a consideration, or for a retired AI result, what the
 * model answered and the prompt it got.
 */
export const HomeEvalResultDetails: FC<{ result: HomeEvalResult; onBack?: () => void }> = ({ result, onBack }) => (
    <div className="space-y-6">
        <Card>
            <CardHeader className="border-b-1 pb-3 flex flex-row items-center">
                {onBack ? (
                    <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                ) : null}
                <CardTitle className="text-xl">Result Details</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <ResultMetadata result={result} />
                    {result.schema === 1 ? (
                        <HomeEvalAiResponseDetails response={result.aiResponse} />
                    ) : (
                        <QueryEvalDetails result={result} />
                    )}
                </div>
            </CardContent>
        </Card>

        {result.schema === 1 ? <HomeEvalAiContextCard aiContext={result.aiContext} /> : null}

        <Card>
            <CardHeader>
                <CardTitle>Result Object</CardTitle>
                <CardDescription>The complete result stored in Firestore.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                    <JsonViewer data={result} />
                </div>
            </CardContent>
        </Card>
    </div>
);

const ResultMetadata: FC<{ result: HomeEvalResult }> = ({ result }) => (
    <div className="grid gap-4 md:grid-cols-2">
        <MetadataItem label="Result ID" value={<IdWithCopy id={result.id} variant="mono" />} />
        <MetadataItem
            label="Home ID"
            value={<IdWithCopy id={result.homeId} variant="mono" to={`/directory/homes/${result.homeId}`} />}
        />
        <MetadataItem
            label="Consideration Type ID"
            value={
                <IdWithCopy
                    id={result.considerationTypeId}
                    variant="mono"
                    to={`/content/considerations/${result.considerationTypeId}`}
                />
            }
        />
        <MetadataItem label="Created At" value={new Date(result.createdAt).toLocaleString()} />
        <MetadataItem
            label="Created Considerations"
            value={
                result.createdConsiderationIds?.length ? (
                    <div className="flex flex-col gap-1">
                        {result.createdConsiderationIds.map((id) => (
                            <IdWithCopy key={id} id={id} variant="mono" />
                        ))}
                    </div>
                ) : (
                    '-'
                )
            }
        />
    </div>
);
